/**
 * MentionEditor — mini-editor TipTap com suporte a @-mention.
 *
 * Parece um textarea simples (sem toolbar). Quando o usuario digita @,
 * aparece um popup com membros do escritorio filtráveis. Ao selecionar,
 * gera um chip com data-mention + data-user-id no HTML.
 *
 * Serialização: HTML (compatível com o campo `description` do backend).
 */
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './mention-editor.css';

export interface MentionMember {
  id: string;
  name: string;
}

interface MentionEditorProps {
  value: string;
  onChange: (html: string) => void;
  members: MentionMember[];
  placeholder?: string;
  rows?: number;
  className?: string;
}

interface SuggestionItem {
  id: string;
  name: string;
}

// Componente de popup de sugestoes (renderizado via portal)
interface SuggestionListProps {
  items: SuggestionItem[];
  selectedIndex: number;
  onSelect: (item: SuggestionItem) => void;
  position: { top: number; left: number };
}

function SuggestionList({ items, selectedIndex, onSelect, position }: SuggestionListProps) {
  if (items.length === 0) return null;
  return createPortal(
    <div
      className="fixed z-[9999] w-56 rounded-md border border-[var(--border)] bg-[var(--card-bg)] shadow-lg overflow-hidden"
      style={{ top: position.top, left: position.left }}
    >
      {items.map((item, idx) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item)}
          className={`w-full text-left px-3 py-2 text-sm transition-colors ${
            idx === selectedIndex
              ? 'bg-blue-600 text-white'
              : 'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]'
          }`}
        >
          {item.name}
        </button>
      ))}
    </div>,
    document.body,
  );
}

// Handler de sugestoes — referenciado pelo Mention.configure
interface SuggestionState {
  items: SuggestionItem[];
  selectedIndex: number;
  position: { top: number; left: number };
  command: ((item: SuggestionItem) => void) | null;
}

const EMPTY_SUGGESTION: SuggestionState = {
  items: [],
  selectedIndex: 0,
  position: { top: 0, left: 0 },
  command: null,
};

export function MentionEditor({
  value,
  onChange,
  members,
  placeholder = 'Descrição…',
  rows = 3,
  className = '',
}: MentionEditorProps) {
  const [suggestion, setSuggestion] = useState<SuggestionState>(EMPTY_SUGGESTION);
  const membersRef = useRef<MentionMember[]>(members);
  // Manter ref atualizada sem recriar o editor
  useEffect(() => { membersRef.current = members; }, [members]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
        link: {
          autolink: true,
          openOnClick: false,
          HTMLAttributes: {
            class: 'text-[var(--accent)] underline',
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        },
      }),
      Placeholder.configure({ placeholder }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention-chip',
          'data-mention': '',
        },
        renderHTML({ node }) {
          return [
            'span',
            {
              'data-mention': '',
              'data-user-id': node.attrs.id as string,
              class:
                'mention-chip inline-block rounded bg-yellow-100 text-yellow-800 px-1 text-sm font-medium',
            },
            `@${node.attrs.label as string}`,
          ];
        },
        suggestion: {
          char: '@',
          allowedPrefixes: null,
          items: ({ query }: { query: string }) => {
            const q = query.toLowerCase();
            return membersRef.current
              .filter((m) => m.name.toLowerCase().includes(q))
              .slice(0, 8)
              .map((m) => ({ id: m.id, name: m.name }));
          },
          render: () => {
            return {
              onStart: (props: SuggestionProps<SuggestionItem>) => {
                const rect = props.clientRect?.();
                setSuggestion({
                  items: props.items,
                  selectedIndex: 0,
                  position: {
                    top: (rect?.bottom ?? 0) + window.scrollY + 4,
                    left: (rect?.left ?? 0) + window.scrollX,
                  },
                  command: (item: SuggestionItem) =>
                    props.command({ id: item.id, label: item.name }),
                });
              },
              onUpdate: (props: SuggestionProps<SuggestionItem>) => {
                const rect = props.clientRect?.();
                setSuggestion((prev) => ({
                  ...prev,
                  items: props.items,
                  selectedIndex: 0,
                  position: {
                    top: (rect?.bottom ?? 0) + window.scrollY + 4,
                    left: (rect?.left ?? 0) + window.scrollX,
                  },
                  command: (item: SuggestionItem) =>
                    props.command({ id: item.id, label: item.name }),
                }));
              },
              onKeyDown: ({ event }: SuggestionKeyDownProps) => {
                if (event.key === 'ArrowDown') {
                  setSuggestion((prev) => ({
                    ...prev,
                    selectedIndex: (prev.selectedIndex + 1) % Math.max(1, prev.items.length),
                  }));
                  return true;
                }
                if (event.key === 'ArrowUp') {
                  setSuggestion((prev) => ({
                    ...prev,
                    selectedIndex:
                      (prev.selectedIndex - 1 + prev.items.length) % Math.max(1, prev.items.length),
                  }));
                  return true;
                }
                if (event.key === 'Enter') {
                  setSuggestion((prev) => {
                    const item = prev.items[prev.selectedIndex];
                    if (item && prev.command) prev.command(item);
                    return EMPTY_SUGGESTION;
                  });
                  return true;
                }
                if (event.key === 'Escape') {
                  setSuggestion(EMPTY_SUGGESTION);
                  return true;
                }
                return false;
              },
              onExit: () => {
                setSuggestion(EMPTY_SUGGESTION);
              },
            };
          },
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      // Conteudo vazio -> string vazia
      const html = ed.isEmpty ? '' : ed.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[inherit] prose-none',
      },
    },
  });

  // Sincroniza value externo com o editor (modo edicao: popula uma vez)
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!editor || hydratedRef.current) return;
    if (value) {
      editor.commands.setContent(value);
      hydratedRef.current = true;
    }
  }, [editor, value]);

  return (
    <div className={`relative ${className}`}>
      <div
        className="mention-editor-container w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] cursor-text"
        style={{ minHeight: '100px', maxHeight: '240px', overflowY: 'auto' }}
        onClick={() => editor?.commands.focus()}
      >
        <EditorContent editor={editor} className="mention-editor-content" />
      </div>
      {suggestion.items.length > 0 && suggestion.command && (
        <SuggestionList
          items={suggestion.items}
          selectedIndex={suggestion.selectedIndex}
          position={suggestion.position}
          onSelect={(item) => {
            suggestion.command!(item);
            setSuggestion(EMPTY_SUGGESTION);
          }}
        />
      )}
    </div>
  );
}
